import React, { useState, useRef, useEffect } from 'react';
import { X, Send, BookOpen, ChevronLeft, Sparkles } from 'lucide-react';
import { OLD_TESTAMENT, NEW_TESTAMENT } from '../data/bibleBooks';
import { parseReference } from '../utils/bibleReference';
import { searchVerses } from '../utils/verseSearch';
import './VerseFinder.css';

const TOPIC_SUGGESTIONS = ['Angst & Sorge', 'Dankbarkeit', 'Vergebung', 'Hoffnung', 'Einsamkeit', 'Geduld'];

function BookGrid({ books, onSelect }) {
  return (
    <div className="vf-book-grid">
      {books.map(book => (
        <button key={book.id} className="vf-book-tile" onClick={() => onSelect(book)}>
          {book.label}
        </button>
      ))}
    </div>
  );
}

function VerseFinder({ onClose, onNavigate }) {
  const [mode, setMode] = useState('browse'); // 'browse' | 'chapters' | 'results'
  const [selectedBook, setSelectedBook] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);

  // Autofocus the input so the mobile keyboard opens immediately.
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 280);
    return () => clearTimeout(t);
  }, []);

  // Lock background scroll while the overlay is open; close on Escape.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const parsed = parseReference(query);

  const runSearch = async (q) => {
    setLoading(true);
    setError('');
    setResults([]);
    setMode('results');
    try {
      const r = await searchVerses(q);
      setResults(r);
    } catch (e) {
      setError(e.message || 'Die Suche ist fehlgeschlagen.');
    }
    setLoading(false);
  };

  const handleSubmit = () => {
    const q = query.trim();
    if (!q || loading) return;
    const ref = parseReference(q);
    if (ref) {
      onNavigate({ book: ref.book, chapter: ref.chapter, verse: ref.verse });
      return;
    }
    runSearch(q);
  };

  const handleTopic = (topic) => {
    setQuery(topic);
    runSearch(topic);
  };

  const handleBookSelect = (book) => {
    setSelectedBook(book);
    setMode('chapters');
  };

  const handleChapterSelect = (chapter) => {
    onNavigate({ book: selectedBook.name, chapter, verse: null });
  };

  const handleResultSelect = (reference) => {
    const ref = parseReference(reference);
    if (ref) onNavigate({ book: ref.book, chapter: ref.chapter, verse: ref.verse });
  };

  const headerTitle =
    mode === 'chapters' ? selectedBook?.label :
    mode === 'results' ? 'Passende Bibelstellen' :
    'In die Bibel einsteigen';

  return (
    <div className="vf-overlay">
      <div className="vf-header">
        {mode === 'chapters' ? (
          <button className="vf-icon-btn" onClick={() => setMode('browse')} aria-label="Zurück">
            <ChevronLeft size={20} />
          </button>
        ) : (
          <span className="vf-icon-btn-spacer" />
        )}
        <h2 className="vf-header-title">{headerTitle}</h2>
        <button className="vf-icon-btn" onClick={onClose} aria-label="Schließen">
          <X size={20} />
        </button>
      </div>

      <div className="vf-body">
        {mode === 'browse' && (
          <>
            <p className="vf-section-title">Wonach ist dir heute?</p>
            <div className="vf-topic-chips">
              {TOPIC_SUGGESTIONS.map(t => (
                <button key={t} className="vf-topic-chip" onClick={() => handleTopic(t)}>
                  <Sparkles size={12} />
                  {t}
                </button>
              ))}
            </div>

            <p className="vf-section-title">Altes Testament</p>
            <BookGrid books={OLD_TESTAMENT} onSelect={handleBookSelect} />

            <p className="vf-section-title">Neues Testament</p>
            <BookGrid books={NEW_TESTAMENT} onSelect={handleBookSelect} />
          </>
        )}

        {mode === 'chapters' && selectedBook && (
          <>
            <p className="vf-section-title">Kapitel wählen</p>
            <div className="vf-chapter-grid">
              {Array.from({ length: selectedBook.chapters }, (_, i) => i + 1).map(ch => (
                <button key={ch} className="vf-chapter-tile" onClick={() => handleChapterSelect(ch)}>
                  {ch}
                </button>
              ))}
            </div>
          </>
        )}

        {mode === 'results' && (
          <>
            <button className="vf-results-back" onClick={() => setMode('browse')}>
              <ChevronLeft size={14} />
              Zurück zur Übersicht
            </button>

            {loading && (
              <div className="vf-loading">
                <span className="vf-loading-dot" />
                <span className="vf-loading-dot" />
                <span className="vf-loading-dot" />
                <span className="vf-loading-text">Passende Stellen werden gesucht…</span>
              </div>
            )}

            {error && <p className="vf-error">{error}</p>}

            {!loading && !error && results.length === 0 && (
              <p className="vf-empty">Keine Vorschläge gefunden. Formuliere dein Anliegen anders.</p>
            )}

            {!loading && results.length > 0 && (
              <>
                <p className="vf-results-note">
                  Vorschläge zu „{query.trim()}" — tippe eine Stelle an, um sie zu öffnen.
                </p>
                <div className="vf-results">
                  {results.map((r, i) => {
                    const ref = parseReference(r.reference);
                    return (
                      <button
                        key={i}
                        className={`vf-result-card${ref ? '' : ' vf-result-card--disabled'}`}
                        onClick={() => ref && handleResultSelect(r.reference)}
                        disabled={!ref}
                      >
                        <div className="vf-result-head">
                          <BookOpen size={14} />
                          <span className="vf-result-ref">{r.reference}</span>
                        </div>
                        <p className="vf-result-reason">{r.reason}</p>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="vf-input-area">
        {parsed && (
          <p className="vf-input-hint">
            Bibelstelle erkannt — öffnet {parsed.label} {parsed.chapter}
            {parsed.verse ? `,${parsed.verse}` : ''}
          </p>
        )}
        <div className="vf-input-border">
          <div className="vf-input-inner">
            <BookOpen size={18} className="vf-input-icon" />
            <input
              ref={inputRef}
              type="text"
              className="vf-input"
              placeholder="Bibelstelle oder Anliegen eingeben…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
            <button
              className="vf-send"
              onClick={handleSubmit}
              disabled={!query.trim() || loading}
              aria-label="Suchen"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerseFinder;
